import { useCallback, useEffect, useRef, useState } from "react";

import { getCopilotAuthToken } from "./authToken";

// Poll cadence while the panel is open. A usage meter doesn't need sub-30s
// freshness; threshold crossings (budget notices) and session-end trigger an
// immediate refetch on top of this. Polling stops when the panel closes.
const POLL_MS = 30000;

/**
 * Fetches the current daily usage snapshot from the copilot server's
 * `GET /api/usage` for the in-panel usage meter.
 *
 * @param {string} serverUrl - copilot server base URL (same as /api/offer).
 * @param {boolean} enabled - poll only while the panel is open.
 * @returns {{ usage: object|null, error: Error|null, refetch: () => void }}
 */
export function useCopilotUsage(serverUrl, enabled) {
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const refetch = useCallback(async () => {
    if (!serverUrl) return;
    // Cancel any in-flight request so a slow poll can't clobber a fresh one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const headers = { Accept: "application/json" };
      // Authenticated deployments need the Bearer token so the server can
      // attribute usage to the caller; dev/auth-off returns null and the
      // server falls back to the anonymous principal.
      let token = null;
      try {
        token = await getCopilotAuthToken();
      } catch {
        token = null;
      }
      if (token) headers.Authorization = `Bearer ${token}`;

      const base = serverUrl.replace(/\/+$/, "");
      const res = await fetch(`${base}/api/usage`, {
        method: "GET",
        headers,
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`usage request failed: ${res.status}`);
      setUsage(await res.json());
      setError(null);
    } catch (err) {
      if (err?.name === "AbortError") return;
      // eslint-disable-next-line no-console
      console.warn("[copilot] usage.fetch_failed", err?.message ?? String(err));
      setError(err);
    }
  }, [serverUrl]);

  useEffect(() => {
    if (!enabled) return undefined;
    refetch();
    const id = window.setInterval(refetch, POLL_MS);
    return () => {
      window.clearInterval(id);
      abortRef.current?.abort();
    };
  }, [enabled, refetch]);

  return { usage, error, refetch };
}
